import type {
  AdminAiGrant,
  AdminAiGrantUpsertInput,
  AdminAiInvokeInput,
  AdminAiModel,
  AdminAiModelCreateInput,
  AdminAiModelUpdateInput,
  AdminAiProvider,
  AdminAiProviderCreateInput,
  AdminAiProviderUpdateInput,
  AdminAiUsage,
  AdminAiUsageQuery,
  AiCapability,
  AiInvokeResult,
} from "@yahshi/contracts";
import { adminAiProviderCreateSchema, adminAiProviderUpdateSchema } from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { AiModelApi } from "./AiModelApi";

type AppLookup = { id: string; name: string; code: string; status: string };

const STAMP = "2026-09-15T00:00:00.000Z";

function mask(ref: string): string {
  return ref ? ref.replace(/[^/]+$/, "***") : "";
}

function shanghaiPeriod(now: Date): string {
  return now.toLocaleString("sv-SE", { timeZone: "Asia/Shanghai" }).slice(0, 7);
}

function nextId(prefix: string): string {
  return `${prefix}${crypto.randomUUID().replaceAll("-", "").slice(0, 24)}`;
}

function demoOutput(capability: AiCapability, prompt: string): { text: string | null; mediaUrl: string | null } {
  const clip = prompt.replace(/\s+/g, " ").slice(0, 80);
  if (capability === "text_generation") return { text: `[演示] ${clip}`, mediaUrl: null };
  if (capability === "speech_recognition") return { text: `[演示转写] ${clip}`, mediaUrl: null };
  if (capability === "speech_synthesis") return { text: null, mediaUrl: "https://demo.yahshi.cn/ai/tts/demo.mp3" };
  return { text: null, mediaUrl: "https://demo.yahshi.cn/ai/image/demo.png" };
}

function seedProviders(): Array<AdminAiProvider & { secretRef: string; storedKey: string }> {
  return [
    { id: "aip_demo_text", name: "演示文本网关", code: "demo-text", status: "active", gateway: "demo", baseUrl: "", credentialConfigured: false, credentialRef: "", secretRef: "", storedKey: "", version: 1, updatedAt: STAMP },
    { id: "aip_demo_media", name: "演示语音图像网关", code: "demo-media", status: "active", gateway: "demo", baseUrl: "", credentialConfigured: true, credentialRef: "secret://ai/***", secretRef: "secret://ai/demo-media", storedKey: "", version: 1, updatedAt: STAMP },
  ];
}

function seedModels(): AdminAiModel[] {
  return [
    { id: "aim_demo_text", providerId: "aip_demo_text", providerName: "演示文本网关", name: "通用对话", nameUg: "ئادەتتىكى سۆھبەت", modelKey: "demo-chat", capability: "text_generation", status: "published", version: 1, updatedAt: STAMP },
    { id: "aim_demo_asr", providerId: "aip_demo_media", providerName: "演示语音图像网关", name: "语音转写", nameUg: "ئاۋازنى يېزىققا ئايلاندۇرۇش", modelKey: "demo-asr", capability: "speech_recognition", status: "published", version: 1, updatedAt: STAMP },
    { id: "aim_demo_tts", providerId: "aip_demo_media", providerName: "演示语音图像网关", name: "语音合成", nameUg: "ئاۋاز ھاسىل قىلىش", modelKey: "demo-tts", capability: "speech_synthesis", status: "published", version: 1, updatedAt: STAMP },
    { id: "aim_demo_image", providerId: "aip_demo_media", providerName: "演示语音图像网关", name: "图片生成", nameUg: "رەسىم ھاسىل قىلىش", modelKey: "demo-image", capability: "image_generation", status: "published", version: 1, updatedAt: STAMP },
    { id: "aim_demo_draft", providerId: "aip_demo_text", providerName: "演示文本网关", name: "草稿对话", nameUg: "سۆھبەت لايىھەسى", modelKey: "demo-draft", capability: "text_generation", status: "draft", version: 1, updatedAt: STAMP },
  ];
}

function seedGrants(): AdminAiGrant[] {
  const web = { appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web" };
  const admin = { appId: "app_yahshi_admin", appName: "Yahshi Admin", appCode: "yahshi-admin" };
  return [
    { ...web, modelId: "aim_demo_text", modelName: "通用对话", capability: "text_generation", enabled: true, monthlyQuota: null, usedThisMonth: 12, version: 1, updatedAt: STAMP },
    { ...web, modelId: "aim_demo_asr", modelName: "语音转写", capability: "speech_recognition", enabled: true, monthlyQuota: null, usedThisMonth: 0, version: 1, updatedAt: STAMP },
    { ...web, modelId: "aim_demo_tts", modelName: "语音合成", capability: "speech_synthesis", enabled: true, monthlyQuota: null, usedThisMonth: 0, version: 1, updatedAt: STAMP },
    { ...web, modelId: "aim_demo_image", modelName: "图片生成", capability: "image_generation", enabled: true, monthlyQuota: null, usedThisMonth: 3, version: 1, updatedAt: STAMP },
    { ...admin, modelId: "aim_demo_text", modelName: "通用对话", capability: "text_generation", enabled: true, monthlyQuota: 200, usedThisMonth: 0, version: 1, updatedAt: STAMP },
  ];
}

function seedUsage(period: string): AdminAiUsage[] {
  return [
    { appId: "app_yahshi_web", appName: "Yahshi Web", modelId: "aim_demo_text", modelName: "通用对话", capability: "text_generation", period, callCount: 12, unitCount: 12 },
    { appId: "app_yahshi_web", appName: "Yahshi Web", modelId: "aim_demo_image", modelName: "图片生成", capability: "image_generation", period, callCount: 3, unitCount: 3 },
  ];
}

export function createDevelopmentAiModelApi(options: {
  now?: () => Date;
  getApp: (id: string) => Promise<AppLookup>;
} ): AiModelApi {
  const now = () => options.now?.() ?? new Date();
  const providers = seedProviders();
  const models = seedModels();
  const grants = seedGrants();
  const usage = seedUsage(shanghaiPeriod(now()));

  const publicProvider = (row: (typeof providers)[number]): AdminAiProvider => {
    const { secretRef: _secret, storedKey: _stored, ...rest } = row;
    const configured = row.storedKey !== "" || row.secretRef !== "";
    return {
      ...rest,
      gateway: row.gateway,
      baseUrl: row.baseUrl,
      credentialConfigured: configured,
      credentialRef: row.storedKey ? "secret://ai-provider/***" : mask(row.secretRef),
    };
  };

  function assignSecret(row: (typeof providers)[number], apiKey: string, credentialRef: string) {
    const value = apiKey.trim() || credentialRef.trim();
    if (!value || value.includes("***")) return;
    if (/^(env|secret):\/\/[A-Za-z0-9_./-]+$/.test(value)) {
      row.secretRef = value;
      row.storedKey = "";
      return;
    }
    row.storedKey = value;
    row.secretRef = "";
  }

  function used(appId: string, modelId: string): number {
    const period = shanghaiPeriod(now());
    return usage.find((item) => item.appId === appId && item.modelId === modelId && item.period === period)?.callCount ?? 0;
  }

  function consume(appId: string, model: AdminAiModel, prompt: string): AiInvokeResult {
    const grant = grants.find((item) => item.appId === appId && item.modelId === model.id && item.enabled);
    if (!grant || model.status !== "published") throw new M1ApiError("AI_NOT_GRANTED", "该应用未授权此模型");
    const provider = providers.find((item) => item.id === model.providerId);
    if (!provider || provider.status !== "active") throw new M1ApiError("AI_PROVIDER_DISABLED", "供应商已停用");
    const period = shanghaiPeriod(now());
    let row = usage.find((item) => item.appId === appId && item.modelId === model.id && item.period === period);
    if (!row) {
      row = { appId, appName: grant.appName, modelId: model.id, modelName: model.name, capability: model.capability, period, callCount: 0, unitCount: 0 };
      usage.push(row);
    }
    if (grant.monthlyQuota != null && row.callCount + 1 > grant.monthlyQuota) throw new M1ApiError("AI_QUOTA", "本月调用额度已用完");
    row.callCount += 1;
    row.unitCount += 1;
    grant.usedThisMonth = row.callCount;
    const output = demoOutput(model.capability, prompt);
    return {
      id: `air_${model.id.slice(-12)}_${row.callCount}`,
      modelId: model.id,
      capability: model.capability,
      demo: true,
      text: output.text,
      mediaUrl: output.mediaUrl,
      usage: { period, callCount: row.callCount, unitCount: row.unitCount, monthlyQuota: grant.monthlyQuota },
    };
  }

  return {
    async listProviders() { return providers.map(publicProvider); },
    async createProvider(input: AdminAiProviderCreateInput) {
      const parsed = adminAiProviderCreateSchema.parse(input);
      if (providers.some((item) => item.code === parsed.code)) throw new M1ApiError("AI_PROVIDER_CONFLICT", "供应商编码已存在");
      const row = {
        id: nextId("aip_"), name: parsed.name, code: parsed.code, status: "active" as const,
        gateway: parsed.gateway, baseUrl: parsed.baseUrl,
        credentialConfigured: false, credentialRef: "",
        secretRef: "", storedKey: "", version: 1, updatedAt: now().toISOString(),
      };
      assignSecret(row, parsed.apiKey, parsed.credentialRef);
      providers.push(row);
      return publicProvider(row);
    },
    async updateProvider(id, input: AdminAiProviderUpdateInput) {
      const parsed = adminAiProviderUpdateSchema.parse(input);
      const row = providers.find((item) => item.id === id);
      if (!row || row.version !== parsed.expectedVersion) throw new M1ApiError("AI_VERSION_CONFLICT", "记录已被更新，请刷新后重试");
      row.name = parsed.name;
      row.status = parsed.status;
      row.gateway = parsed.gateway;
      row.baseUrl = parsed.baseUrl;
      assignSecret(row, parsed.apiKey, parsed.credentialRef);
      row.version += 1;
      row.updatedAt = now().toISOString();
      return publicProvider(row);
    },
    async listModels() { return models.map((item) => ({ ...item })); },
    async createModel(input: AdminAiModelCreateInput) {
      const provider = providers.find((item) => item.id === input.providerId);
      if (!provider) throw new M1ApiError("AI_PROVIDER_NOT_FOUND", "供应商不存在");
      if (models.some((item) => item.providerId === input.providerId && item.modelKey === input.modelKey)) {
        throw new M1ApiError("AI_MODEL_CONFLICT", "同一供应商下模型标识已存在");
      }
      const row: AdminAiModel = {
        id: nextId("aim_"), providerId: provider.id, providerName: provider.name, name: input.name, nameUg: input.nameUg,
        modelKey: input.modelKey, capability: input.capability, status: "draft", version: 1, updatedAt: now().toISOString(),
      };
      models.push(row);
      return { ...row };
    },
    async updateModel(id, input: AdminAiModelUpdateInput) {
      const row = models.find((item) => item.id === id);
      if (!row || row.version !== input.expectedVersion) throw new M1ApiError("AI_VERSION_CONFLICT", "记录已被更新，请刷新后重试");
      row.name = input.name;
      row.nameUg = input.nameUg;
      row.modelKey = input.modelKey;
      row.capability = input.capability;
      if (row.status === "published") row.status = "draft";
      row.version += 1;
      row.updatedAt = now().toISOString();
      return { ...row };
    },
    async publishModel(id, input) {
      const row = models.find((item) => item.id === id);
      if (!row || row.version !== input.expectedVersion || row.status === "disabled") throw new M1ApiError("AI_VERSION_CONFLICT", "记录已被更新，请刷新后重试");
      row.status = "published";
      row.version += 1;
      row.updatedAt = now().toISOString();
      return { ...row };
    },
    async listGrants() {
      return grants.map((item) => ({ ...item, usedThisMonth: used(item.appId, item.modelId) }));
    },
    async upsertGrant(input: AdminAiGrantUpsertInput) {
      const app = await options.getApp(input.appId).catch(() => { throw new M1ApiError("APP_NOT_FOUND", "应用不存在"); });
      if (app.status !== "active") throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      const model = models.find((item) => item.id === input.modelId && item.status === "published");
      if (!model) throw new M1ApiError("AI_MODEL_NOT_FOUND", "模型不存在或未发布");
      const existing = grants.find((item) => item.appId === input.appId && item.modelId === input.modelId);
      if (existing) {
        if (existing.version !== input.expectedVersion) throw new M1ApiError("AI_VERSION_CONFLICT", "记录已被更新，请刷新后重试");
        existing.enabled = input.enabled;
        existing.monthlyQuota = input.monthlyQuota;
        existing.version += 1;
        existing.updatedAt = now().toISOString();
        existing.usedThisMonth = used(existing.appId, existing.modelId);
        return { ...existing };
      }
      if (input.expectedVersion !== 0) throw new M1ApiError("AI_VERSION_CONFLICT", "记录已被更新，请刷新后重试");
      const row: AdminAiGrant = {
        appId: app.id, appName: app.name, appCode: app.code, modelId: model.id, modelName: model.name,
        capability: model.capability, enabled: input.enabled, monthlyQuota: input.monthlyQuota,
        usedThisMonth: used(app.id, model.id), version: 1, updatedAt: now().toISOString(),
      };
      grants.push(row);
      return { ...row };
    },
    async listUsage(query: Partial<AdminAiUsageQuery> = {}) {
      const period = query.period || shanghaiPeriod(now());
      return usage.filter((item) => item.period === period && (!query.appId || item.appId === query.appId)).map((item) => ({ ...item }));
    },
    async invoke(input: AdminAiInvokeInput) {
      await options.getApp(input.appId).catch(() => { throw new M1ApiError("APP_NOT_FOUND", "应用不存在"); });
      const model = models.find((item) => item.id === input.modelId);
      if (!model) throw new M1ApiError("AI_MODEL_NOT_FOUND", "模型不存在或未发布");
      return consume(input.appId, model, input.prompt);
    },
  };
}
